import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import type { AccountRow } from '../../../../shared/src/types';

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1 GB
const MAX_FACEBOOK_ACCOUNTS = 1;
const MAX_TIKTOK_ACCOUNTS = 2;

// ---------------------------------------------------------------------------
// Per-provider publish state
// ---------------------------------------------------------------------------

type ProviderStatus =
  | 'idle'
  | 'preparing'
  | 'uploading'
  | 'processing'
  | 'success'
  | 'failed';

interface ProviderState {
  status: ProviderStatus;
  progress: { uploaded: number; total: number };
  postId: string | null;
  releaseId: string | null;
  error: string | null;
  // TikTok only — upload url is safe to expose in browser (temporary signed url)
  uploadUrl: string | null;
  chunkSize: number | null;
}

const defaultProviderState = (): ProviderState => ({
  status: 'idle',
  progress: { uploaded: 0, total: 0 },
  postId: null,
  releaseId: null,
  error: null,
  uploadUrl: null,
  chunkSize: null,
});

// ---------------------------------------------------------------------------
// TikTok Settings Interface
// ---------------------------------------------------------------------------
interface TikTokSettings {
  info: any; // creator info from API
  loading: boolean;
  privacyLevel: string;
  allowComments: boolean;
  allowDuet: boolean;
  allowStitch: boolean;
  brandContent: boolean;
  brandOrganic: boolean;
  isAigc: boolean;
}

// ---------------------------------------------------------------------------
// Privacy label map
// ---------------------------------------------------------------------------

const PRIVACY_LABELS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'Public',
  MUTUAL_FOLLOW_FRIENDS: 'Friends',
  FOLLOWER_OF_CREATOR: 'Followers',
  SELF_ONLY: 'Only me',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ComposePage() {
  // Accounts
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // Shared content
  const [file, setFile] = useState<File | null>(null);
  const [content, setContent] = useState('');

  // Account Selection
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());

  // Per-account publish state
  const [publishStates, setPublishStates] = useState<Record<string, ProviderState>>({});

  // Per-account TikTok settings
  const [ttSettings, setTtSettings] = useState<Record<string, TikTokSettings>>({});

  // Abort controllers — one per account
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  // ---------------------------------------------------------------------------
  // Load accounts
  // ---------------------------------------------------------------------------

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/accounts`)
      .then(r => r.json())
      .then((data: any) => {
        const accs: AccountRow[] = data.accounts || [];
        setAccounts(accs);
        
        // Auto-select defaults
        const fb = accs.find(a => a.provider === 'facebook');
        const tt = accs.find(a => a.provider === 'tiktok');
        const defaultSelection = new Set<string>();
        if (fb) defaultSelection.add(fb.id);
        if (tt) defaultSelection.add(tt.id);
        setSelectedAccounts(defaultSelection);
      })
      .catch(err => console.error('Failed to load accounts', err))
      .finally(() => setLoadingAccounts(false));
  }, []);

  // ---------------------------------------------------------------------------
  // TikTok creator info (Fetched per account)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const selectedTtAccounts = Array.from(selectedAccounts).filter(id => {
      const acc = accounts.find(a => a.id === id);
      return acc?.provider === 'tiktok' && !ttSettings[id];
    });

    selectedTtAccounts.forEach(id => {
      setTtSettings(prev => ({
        ...prev,
        [id]: {
          info: null,
          loading: true,
          privacyLevel: '',
          allowComments: true,
          allowDuet: true,
          allowStitch: true,
          brandContent: false,
          brandOrganic: false,
          isAigc: false,
        }
      }));

      fetch(`${API_BASE_URL}/api/publish/tiktok/creator-info?account_id=${id}`)
        .then(r => r.json())
        .then((data: any) => {
          if (!data.error) {
            const opts: string[] = Array.isArray(data.privacy_level_options)
              ? data.privacy_level_options
              : [];
            let priv = '';
            if (opts.includes('SELF_ONLY')) {
              priv = 'SELF_ONLY';
            } else if (opts.length > 0) {
              priv = opts[0];
            }

            setTtSettings(prev => ({
              ...prev,
              [id]: {
                ...prev[id],
                loading: false,
                info: data,
                privacyLevel: priv,
                allowComments: !data.comment_disabled,
                allowDuet: !data.duet_disabled,
                allowStitch: !data.stitch_disabled,
              }
            }));
          } else {
            setTtSettings(prev => ({ ...prev, [id]: { ...prev[id], loading: false } }));
          }
        })
        .catch(err => {
          console.error(`Failed to load TT info for ${id}`, err);
          setTtSettings(prev => ({ ...prev, [id]: { ...prev[id], loading: false } }));
        });
    });
  }, [selectedAccounts, accounts, ttSettings]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const isAnyWorking = Object.values(publishStates).some(
    state => ['preparing', 'uploading', 'processing'].includes(state.status)
  );

  const selectedCount = selectedAccounts.size;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const toggleAccount = (account: AccountRow) => {
    const next = new Set(selectedAccounts);
    if (next.has(account.id)) {
      next.delete(account.id);
      setSelectedAccounts(next);
      return;
    }

    // Enforce limits
    const currFbCount = Array.from(next).filter(id => accounts.find(a => a.id === id)?.provider === 'facebook').length;
    const currTtCount = Array.from(next).filter(id => accounts.find(a => a.id === id)?.provider === 'tiktok').length;

    if (account.provider === 'facebook' && currFbCount >= MAX_FACEBOOK_ACCOUNTS) {
      alert(`You can only select up to ${MAX_FACEBOOK_ACCOUNTS} Facebook account(s).`);
      return;
    }
    if (account.provider === 'tiktok' && currTtCount >= MAX_TIKTOK_ACCOUNTS) {
      alert(`You can only select up to ${MAX_TIKTOK_ACCOUNTS} TikTok account(s).`);
      return;
    }

    next.add(account.id);
    setSelectedAccounts(next);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (f.size > MAX_FILE_SIZE) {
      alert('File exceeds 1 GB limit.');
      e.target.value = '';
      setFile(null);
      return;
    }
    setFile(f);
  };

  const updateTtSetting = (accountId: string, key: keyof TikTokSettings, value: any) => {
    setTtSettings(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [key]: value
      }
    }));
  };

  // ---------------------------------------------------------------------------
  // Upload chunks — parameterised by setter
  // ---------------------------------------------------------------------------

  const uploadChunks = async (
    provider: 'facebook' | 'tiktok',
    postId: string,
    startOffset: number,
    setState: React.Dispatch<React.SetStateAction<ProviderState>>,
    abortRef: React.MutableRefObject<AbortController | null>,
    uploadUrl?: string | null,
    chunkSize?: number | null,
    fileRef?: File,
  ) => {
    const f = fileRef ?? file;
    if (!f) return;

    setState(s => ({ ...s, status: 'uploading', progress: { uploaded: startOffset, total: f.size } }));

    const effectiveChunkSize = provider === 'tiktok'
      ? (chunkSize || 10 * 1024 * 1024)
      : 64 * 1024 * 1024;

    let offset = startOffset;
    let retries = 0;

    while (offset < f.size) {
      const chunkEnd = Math.min(offset + effectiveChunkSize, f.size);
      const chunk = f.slice(offset, chunkEnd);

      let res: Response;
      try {
        if (provider === 'tiktok' && uploadUrl) {
          res = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': f.type || 'video/mp4',
              'Content-Range': `bytes ${offset}-${chunkEnd - 1}/${f.size}`,
              'Content-Length': (chunkEnd - offset).toString(),
            },
            body: chunk,
            signal: abortRef.current?.signal,
          });
        } else {
          res = await fetch(`${API_BASE_URL}/api/publish/facebook/chunk`, {
            method: 'POST',
            headers: {
              'x-post-id': postId,
              'x-offset': offset.toString(),
            },
            body: chunk,
            signal: abortRef.current?.signal,
          });
        }
      } catch (err: any) {
        if (err.name === 'AbortError') throw err;
        retries++;
        if (retries > 3) throw new Error(`Network failure after 3 retries: ${err.message}`);
        await new Promise(r => setTimeout(r, 1000 * retries));
        continue;
      }

      if (provider === 'tiktok') {
        if (!res.ok && res.status !== 206) {
          throw new Error(`TikTok direct upload failed (possible CORS/network restriction): HTTP ${res.status}`);
        }
        offset = chunkEnd;
        setState(s => ({ ...s, progress: { uploaded: offset, total: f.size } }));
        retries = 0;
      } else {
        const data = await res.json().catch(() => ({})) as any;
        if (!res.ok && res.status !== 206) {
          throw new Error(data.error || 'Failed to upload chunk');
        }
        if (data.next_offset != null) {
          offset = data.next_offset;
          setState(s => ({ ...s, progress: { uploaded: offset, total: f.size } }));
          retries = 0;
        }
        if (data.complete || offset >= f.size) break;
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Poll status
  // ---------------------------------------------------------------------------

  const pollStatus = async (
    provider: 'facebook' | 'tiktok',
    postId: string,
    setState: React.Dispatch<React.SetStateAction<ProviderState>>,
    abortRef: React.MutableRefObject<AbortController | null>,
  ) => {
    setState(s => ({ ...s, status: 'processing' }));
    let attempts = 0;
    const maxAttempts = 60;
    const delay = provider === 'tiktok' ? 5000 : 4000;

    while (attempts < maxAttempts) {
      attempts++;
      await new Promise(r => setTimeout(r, delay));
      if (abortRef.current?.signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const r = await fetch(`${API_BASE_URL}/api/publish/${provider}/status?post_id=${postId}`, {
        signal: abortRef.current?.signal,
      });
      if (!r.ok) continue;

      const data = await r.json() as any;
      if (data.state === 'published') {
        setState(s => ({ ...s, status: 'success', releaseId: data.release_id ?? null }));
        return;
      }
      if (data.state === 'failed') {
        throw new Error(
          `${provider === 'tiktok' ? 'TikTok' : 'Meta'} processing failed. ` +
          (data.error_message || data.meta_status || '')
        );
      }
    }
    throw new Error('Status polling timed out after 5 minutes.');
  };

  // ---------------------------------------------------------------------------
  // Publish a single account
  // ---------------------------------------------------------------------------

  const publishAccount = async (account: AccountRow, fileRef: File) => {
    const accountId = account.id;
    const provider = account.provider;

    abortControllersRef.current[accountId] = new AbortController();
    const abortRef = { current: abortControllersRef.current[accountId] };

    const setState = (updater: React.SetStateAction<ProviderState>) => {
      setPublishStates(prev => {
        const oldState = prev[accountId] || defaultProviderState();
        const newState = typeof updater === 'function' ? updater(oldState) : updater;
        return { ...prev, [accountId]: newState };
      });
    };

    setState(s => ({ ...s, status: 'preparing', error: null, progress: { uploaded: 0, total: fileRef.size } }));

    try {
      // --- TikTok pre-flight ---
      if (provider === 'tiktok') {
        const ts = ttSettings[accountId];
        if (!ts || !ts.info) {
          throw new Error('TikTok settings not loaded. Cannot publish.');
        }
        if ((ts.info.privacy_level_options?.length ?? 0) === 0) {
          throw new Error('TikTok did not return any available privacy levels. Cannot publish.');
        }
        if (!ts.privacyLevel) {
          throw new Error('Please select a privacy level before publishing.');
        }
        if (!ts.info.privacy_level_options.includes(ts.privacyLevel)) {
          throw new Error(`Privacy level "${ts.privacyLevel}" is not in the options returned by TikTok.`);
        }
        // Duration check
        const duration = await new Promise<number>(resolve => {
          const vid = document.createElement('video');
          vid.preload = 'metadata';
          vid.onloadedmetadata = () => resolve(vid.duration);
          vid.onerror = () => resolve(0);
          vid.src = URL.createObjectURL(fileRef);
        });
        if (duration > ts.info.max_video_post_duration_sec) {
          throw new Error(
            `Video duration (${Math.round(duration)}s) exceeds TikTok maximum of ${ts.info.max_video_post_duration_sec}s.`
          );
        }
      }

      // --- Init ---
      const initBody: any = {
        account_id: accountId,
        file_size: fileRef.size,
        content,
      };

      if (provider === 'tiktok') {
        const ts = ttSettings[accountId];
        initBody.privacy_level = ts.privacyLevel;
        initBody.disable_comment = !ts.allowComments;
        initBody.disable_duet = !ts.allowDuet;
        initBody.disable_stitch = !ts.allowStitch;
        initBody.brand_content_toggle = ts.brandContent;
        initBody.brand_organic_toggle = ts.brandOrganic;
        initBody.is_aigc = ts.isAigc;
      }

      const initRes = await fetch(`${API_BASE_URL}/api/publish/${provider}/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(initBody),
        signal: abortRef.current.signal,
      });

      const initData = await initRes.json() as any;

      if (!initRes.ok) {
        // Structured TikTok error
        if (provider === 'tiktok' && initData.tiktok_error) {
          const te = initData.tiktok_error;
          const rd = initData.request_diagnostics || {};
          const lines = [
            `TikTok init failed`,
            `Code: ${te.code ?? 'unknown'}`,
            `Message: ${te.message ?? initData.error}`,
            te.log_id ? `Log ID: ${te.log_id}` : null,
            `HTTP status: ${te.http_status ?? initRes.status}`,
            ``,
            `Request sent:`,
            `  privacy_level: ${rd.privacy_level}`,
            `  brand_content: ${rd.brand_content_toggle}`,
            `  brand_organic: ${rd.brand_organic_toggle}`,
            `  is_aigc: ${rd.is_aigc}`,
            `  video_size: ${rd.video_size} bytes`,
            `  chunk_size: ${rd.chunk_size} bytes`,
            `  total_chunks: ${rd.total_chunk_count}`,
          ].filter(l => l !== null).join('\n');
          throw new Error(lines);
        }
        throw new Error(initData.error || 'Failed to initialize upload');
      }

      const postId = initData.post_id;
      setState(s => ({
        ...s,
        postId,
        uploadUrl: initData.upload_url ?? null,
        chunkSize: initData.chunk_size ?? null,
      }));

      // --- Upload chunks ---
      await uploadChunks(
        provider,
        postId,
        0,
        setState,
        abortRef,
        initData.upload_url,
        initData.chunk_size,
        fileRef,
      );

      // --- Facebook finish step ---
      if (provider === 'facebook') {
        setState(s => ({ ...s, status: 'processing' }));
        const finishRes = await fetch(`${API_BASE_URL}/api/publish/facebook/finish`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ post_id: postId }),
          signal: abortRef.current.signal,
        });
        const finishData = await finishRes.json() as any;
        if (!finishRes.ok) throw new Error(finishData.error || 'Failed to finish publish');
      }

      // --- Poll ---
      await pollStatus(provider, postId, setState, abortRef);

    } catch (err: any) {
      if (err.name === 'AbortError') {
        setState(s => ({ ...s, status: 'failed', error: 'Upload canceled.' }));
      } else {
        setState(s => ({ ...s, status: 'failed', error: err.message || 'An error occurred.' }));
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Handle submit — runs selected accounts concurrently
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    if (selectedAccounts.size === 0) return;

    const tasks: Promise<void>[] = [];

    for (const accountId of Array.from(selectedAccounts)) {
      const account = accounts.find(a => a.id === accountId);
      if (!account) continue;

      setPublishStates(prev => ({ ...prev, [accountId]: defaultProviderState() }));
      tasks.push(publishAccount(account, file));
    }

    await Promise.allSettled(tasks);
  };

  // ---------------------------------------------------------------------------
  // Retry & Cancel per account
  // ---------------------------------------------------------------------------

  const retryAccount = (accountId: string) => {
    if (!file) return;
    const account = accounts.find(a => a.id === accountId);
    if (!account) return;

    setPublishStates(prev => ({ ...prev, [accountId]: defaultProviderState() }));
    publishAccount(account, file);
  };

  const cancelAccount = (accountId: string) => {
    abortControllersRef.current[accountId]?.abort();
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const statusLabel = (s: ProviderStatus) => {
    switch (s) {
      case 'idle':       return null;
      case 'preparing':  return 'Preparing...';
      case 'uploading':  return 'Uploading';
      case 'processing': return 'Processing...';
      case 'success':    return 'Published ✓';
      case 'failed':     return 'Failed';
    }
  };

  const isActive = (s: ProviderState) =>
    ['preparing', 'uploading', 'processing'].includes(s.status);

  const submitLabel = () => {
    if (isAnyWorking) {
      return selectedCount > 1 ? `Publishing to ${selectedCount} accounts...` : 'Publishing...';
    }
    return selectedCount > 1 ? `Publish to ${selectedCount} accounts` : 'Publish to 1 account';
  };

  // ---------------------------------------------------------------------------
  // Provider card component (inline)
  // ---------------------------------------------------------------------------

  const ProviderCard = ({
    label,
    color,
    state,
    accountId,
  }: {
    label: string;
    color: string;
    state: ProviderState;
    accountId: string;
  }) => {
    if (state.status === 'idle') return null;

    const pct = state.progress.total > 0
      ? Math.round((state.progress.uploaded / state.progress.total) * 100)
      : 0;

    return (
      <div style={{
        border: `1px solid ${state.status === 'success' ? '#a5d6a7' : state.status === 'failed' ? '#ef9a9a' : '#ddd'}`,
        borderRadius: '8px',
        padding: '1rem',
        backgroundColor: state.status === 'success' ? '#f1f8e9' : state.status === 'failed' ? '#fff5f5' : '#fafafa',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <strong style={{ color }}>{label}</strong>
          <span style={{ fontSize: '0.875rem', color: state.status === 'success' ? '#2e7d32' : state.status === 'failed' ? '#c62828' : '#555' }}>
            {state.status === 'uploading'
              ? `Uploading ${pct}%`
              : statusLabel(state.status)}
          </span>
        </div>

        {state.status === 'uploading' && (
          <div style={{ width: '100%', backgroundColor: '#eee', height: '6px', borderRadius: '3px', overflow: 'hidden', marginBottom: '0.5rem' }}>
            <div style={{ width: `${pct}%`, backgroundColor: color, height: '100%', transition: 'width 0.2s ease-out' }} />
          </div>
        )}

        {state.status === 'success' && state.releaseId && (
          <div style={{ fontSize: '0.8rem', color: '#555' }}>ID: {state.releaseId}</div>
        )}

        {state.status === 'failed' && state.error && (
          <pre style={{ margin: '0.5rem 0 0', fontFamily: 'inherit', fontSize: '0.8rem', color: '#c62828', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere', maxWidth: '100%', overflowX: 'auto' }}>
            {state.error}
          </pre>
        )}

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          {isActive(state) && (
            <button
              type="button"
              onClick={() => cancelAccount(accountId)}
              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#fff', color: '#d32f2f', border: '1px solid #ef9a9a', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
            >
              Cancel
            </button>
          )}
          {state.status === 'failed' && (
            <button
              type="button"
              onClick={() => retryAccount(accountId)}
              style={{ padding: '0.35rem 0.75rem', backgroundColor: '#fff', color: color, border: `1px solid ${color}`, borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const canSubmit = !isAnyWorking && file && selectedAccounts.size > 0;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.75rem', fontWeight: 600 }}>Compose Post</h1>

      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', backgroundColor: '#fff', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e0e0e0' }}
      >

        {/* ── Account selection ── */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.75rem' }}>Publish to</label>

          {loadingAccounts && <div style={{ color: '#888', fontSize: '0.9rem' }}>Loading accounts...</div>}

          {!loadingAccounts && accounts.length === 0 && (
            <div style={{ color: '#c62828', fontSize: '0.9rem' }}>
              No connected accounts. Go to <a href="/channels">Channels</a> to connect.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {accounts.map(acc => {
              const isSelected = selectedAccounts.has(acc.id);
              const color = acc.provider === 'facebook' ? '#1877F2' : '#010101';
              const bgColor = isSelected ? (acc.provider === 'facebook' ? '#e7f0fd' : '#fff0f3') : '#fafafa';
              const label = acc.provider === 'facebook' ? (acc.page_name || 'Facebook Page') : (acc.display_name || 'TikTok Account');

              return (
                <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: bgColor }}>
                  <input
                    type="checkbox"
                    id={`acc-${acc.id}`}
                    checked={isSelected}
                    onChange={() => toggleAccount(acc)}
                    disabled={isAnyWorking}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor={`acc-${acc.id}`} style={{ fontWeight: 600, color: color, cursor: 'pointer', flex: 1, textTransform: 'capitalize' }}>
                    {acc.provider} <span style={{ fontWeight: 400, color: '#555', marginLeft: '0.5rem' }}>{label}</span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Video File ── */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Video File</label>
          <input
            type="file"
            accept="video/mp4,video/quicktime"
            onChange={handleFileChange}
            disabled={isAnyWorking}
            required
            style={{ width: '100%', padding: '0.5rem 0' }}
          />
          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
            Max size: 1 GB. MP4/MOV.
            {file && <span style={{ marginLeft: '0.5rem', color: '#111' }}>Selected: {(file.size / (1024 * 1024)).toFixed(2)} MB</span>}
          </div>
        </div>

        {/* ── Caption ── */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Description</label>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            disabled={isAnyWorking}
            rows={4}
            placeholder="Write a caption for your video..."
            style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ccc', fontFamily: 'inherit', resize: 'vertical' }}
          />
        </div>

        {/* ── TikTok Settings (Mapped per selected TikTok account) ── */}
        {Array.from(selectedAccounts).filter(id => accounts.find(a => a.id === id)?.provider === 'tiktok').map(accountId => {
          const ts = ttSettings[accountId];
          const acc = accounts.find(a => a.id === accountId);
          if (!ts) return null;

          return (
            <div key={`settings-${accountId}`} style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f9f9f9', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', justifyContent: 'space-between' }}>
                <span>TikTok Settings</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#555' }}>{acc?.display_name || 'Account'}</span>
              </h3>

              {ts.loading && <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading Creator Info...</div>}

              {!ts.loading && ts.info && (
                <>
                  {ts.info.creator_nickname && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {ts.info.creator_avatar_url && (
                        <img src={ts.info.creator_avatar_url} alt="avatar" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      )}
                      <span style={{ fontWeight: 600 }}>{ts.info.creator_nickname}</span>
                      <span style={{ color: '#666' }}>@{ts.info.creator_username}</span>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Privacy Level</label>
                    {(ts.info.privacy_level_options?.length ?? 0) === 0 ? (
                      <div style={{ color: '#c62828', padding: '0.5rem', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '0.9rem' }}>
                        TikTok did not return any available privacy levels.
                      </div>
                    ) : (
                      <select
                        value={ts.privacyLevel}
                        onChange={e => updateTtSetting(accountId, 'privacyLevel', e.target.value)}
                        disabled={isAnyWorking}
                        style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                        required
                      >
                        {(ts.info.privacy_level_options as string[]).map((opt: string) => (
                          <option key={opt} value={opt}>{PRIVACY_LABELS[opt] || opt}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.allowComments} onChange={e => updateTtSetting(accountId, 'allowComments', e.target.checked)} disabled={isAnyWorking || ts.info.comment_disabled} />
                      Allow comments {ts.info.comment_disabled && '(Disabled by creator)'}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.allowDuet} onChange={e => updateTtSetting(accountId, 'allowDuet', e.target.checked)} disabled={isAnyWorking || ts.info.duet_disabled} />
                      Allow duet {ts.info.duet_disabled && '(Disabled by creator)'}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.allowStitch} onChange={e => updateTtSetting(accountId, 'allowStitch', e.target.checked)} disabled={isAnyWorking || ts.info.stitch_disabled} />
                      Allow stitch {ts.info.stitch_disabled && '(Disabled by creator)'}
                    </label>
                  </div>

                  <div style={{ borderTop: '1px solid #ddd', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <strong style={{ fontSize: '0.9rem' }}>Disclosure</strong>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.brandContent} onChange={e => updateTtSetting(accountId, 'brandContent', e.target.checked)} disabled={isAnyWorking} />
                      Brand Content
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.brandOrganic} onChange={e => updateTtSetting(accountId, 'brandOrganic', e.target.checked)} disabled={isAnyWorking} />
                      Brand Organic
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input type="checkbox" checked={ts.isAigc} onChange={e => updateTtSetting(accountId, 'isAigc', e.target.checked)} disabled={isAnyWorking} />
                      AI-Generated Content (AIGC)
                    </label>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* ── Per-account progress cards ── */}
        {Object.keys(publishStates).length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {Object.entries(publishStates).map(([accId, state]) => {
              const acc = accounts.find(a => a.id === accId);
              if (!acc) return null;
              const label = `${acc.provider === 'facebook' ? 'Facebook' : 'TikTok'} — ${acc.provider === 'facebook' ? (acc.page_name || 'Page') : (acc.display_name || 'Account')}`;
              const color = acc.provider === 'facebook' ? '#1877F2' : '#010101';
              return (
                <ProviderCard key={accId} label={label} color={color} state={state} accountId={accId} />
              );
            })}
          </div>
        )}

        {/* ── Submit ── */}
        <div>
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              backgroundColor: canSubmit ? '#1877F2' : '#aaa',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitLabel()}
          </button>
          {selectedAccounts.size === 0 && !loadingAccounts && (
            <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.85rem', color: '#888' }}>
              Select at least one account above.
            </div>
          )}
        </div>

      </form>
    </div>
  );
}